// SectionStack — uniform vertical rhythm between major page sections
// (e.g. Hero, UpcomingList, AnniversaryTimeline). Per MOBILE-UX-goal-prompt.md
// acceptance #1: section-to-section gap is `s-5` / `s-6` (20 / 24 px),
// NEVER a magic number.
//
// Variants:
//   - `loose` (s-6, 24 px) → rich page like anniversaries detail
//   - `default` (s-5, 20 px) → most list / form pages
//   - `compact` (s-4, 16 px) → dense CRUD lists

import type { ReactNode } from "react";

export type SectionStackDensity = "loose" | "default" | "compact";

export interface SectionStackProps {
  children: ReactNode;
  density?: SectionStackDensity;
  className?: string;
  /** Render as `<section>` (true) or `<div>` (false). Use `<section>` for top-level page sections. */
  as?: "section" | "div";
  id?: string;
  /** Optional `aria-labelledby` to wire the section to a heading. */
  "aria-labelledby"?: string;
}

const DENSITY_CLASS: Record<SectionStackDensity, string> = {
  loose: "space-y-s-6",
  default: "space-y-s-5",
  compact: "space-y-s-4",
};

export function SectionStack({
  children,
  density = "default",
  className = "",
  as = "section",
  id,
  "aria-labelledby": ariaLabelledBy,
}: SectionStackProps): JSX.Element {
  const Comp = as;
  return (
    <Comp
      id={id}
      data-component="SectionStack"
      data-density={density}
      aria-labelledby={ariaLabelledBy}
      className={[DENSITY_CLASS[density], className].filter(Boolean).join(" ")}
    >
      {children}
    </Comp>
  );
}

/**
 * RowGap — uniform vertical rhythm between sibling rows inside a section
 * (e.g. cards in a list, fields in a form). LIVES BETWEEN SectionStack
 * children. Per MOBILE-UX-goal-prompt.md acceptance #1: row spacing is
 * `s-3` / `s-4` (12 / 16 px), NEVER a magic number.
 */
export type RowGapDensity = "tight" | "default" | "loose";

export interface RowGapProps {
  children: ReactNode;
  density?: RowGapDensity;
  className?: string;
  as?: "ul" | "ol" | "div" | "section";
}

const ROW_DENSITY_CLASS: Record<RowGapDensity, string> = {
  tight: "space-y-s-3",
  default: "space-y-s-4",
  loose: "space-y-s-5",
};

export function RowGap({
  children,
  density = "default",
  className = "",
  as = "div",
}: RowGapProps): JSX.Element {
  const Comp = as;
  return (
    <Comp
      data-component="RowGap"
      data-density={density}
      className={[ROW_DENSITY_CLASS[density], className].filter(Boolean).join(" ")}
    >
      {children}
    </Comp>
  );
}
